import { Send, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { CapabilityGate } from '../components/CapabilityGate';
import { deriveGuardianship } from '../features/persons/persons-adapter';
import { usePerson } from '../features/persons/use-persons';
import {
  blockedMessage,
  recipientRestrictionMessage,
} from '../features/proactive/proactive-adapter';
import { Step1PessoaCanal, type SelectedPerson } from '../features/proactive/Step1PessoaCanal';
import { Step2Categoria } from '../features/proactive/Step2Categoria';
import { Step3Template } from '../features/proactive/Step3Template';
import { Step4Confirmar } from '../features/proactive/Step4Confirmar';
import { StepRail } from '../features/proactive/StepRail';
import { ProactiveBlockedError } from '../features/proactive/types';
import {
  useInitiateProactive,
  useProactiveContext,
  useRecipientStatus,
} from '../features/proactive/use-proactive';
import { useAuthStore } from '../stores/auth-store';
import { useToastStore } from '../stores/toast-store';

import '../features/proactive/iniciar-conversa-2b.css';

import type {
  ContextOriginType,
  MetaCategory,
  ProactiveOriginType,
  ProactiveTemplate,
} from '../features/proactive/types';
import type { ChannelKind } from '@lume/shared';

const ROLE_LABELS: Record<string, string> = {
  atendente: 'Atendente',
  supervisor: 'Supervisor',
  gestor: 'Gestor',
  admin_tenant: 'Admin',
  dpo_tenant: 'DPO',
};

const CONTEXT_ORIGINS: readonly ContextOriginType[] = [
  'pendency',
  'commitment',
  'appointment',
  'conversation',
];

function parseOrigin(raw: string | null): ProactiveOriginType {
  if (raw === null) return 'cold';
  return (CONTEXT_ORIGINS as readonly string[]).includes(raw)
    ? (raw as ProactiveOriginType)
    : 'cold';
}

/**
 * IniciarConversaPage — rota `/iniciar-conversa` (Brief 2B T8 · ADR-127).
 *
 * Fluxo de 4 passos da iniciação proativa. Fecha os deep-links já vivos: o CTA
 * "Iniciar Conversa"/⌘N da Inbox (cold) e o "Iniciar conversa" da ficha da
 * pessoa (`?personId=`, cold com pessoa pré-selecionada). Origens não-cold
 * (`?originType=&originId=`) pré-preenchem a pessoa via `GET /context`.
 *
 * Layout: o chrome (AppShell) já existe; a tela é uma rota renderizada como card
 * centrado sobre um fundo de listagem borrado — não um overlay fixed (que
 * cobriria sidebar/topbar). Gate de tela: `initiate-proactive-conversation`.
 */
export function IniciarConversaPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const user = useAuthStore((s) => s.user);

  const queryPersonId = params.get('personId');
  const origin = parseOrigin(params.get('originType'));
  const originId = params.get('originId') ?? undefined;

  // Prefill por origem não-cold.
  const { data: context } = useProactiveContext(origin === 'cold' ? undefined : origin, originId);

  // Estado do wizard.
  const [step, setStep] = useState(1);
  const [selected, setSelected] = useState<SelectedPerson | null>(null);
  const [channel, setChannel] = useState<ChannelKind | null>(null);
  const [category, setCategory] = useState<MetaCategory | null>(null);
  const [template, setTemplate] = useState<ProactiveTemplate | null>(null);
  const [vars, setVars] = useState<Record<string, string>>({});
  const [blocked, setBlocked] = useState<string | null>(null);

  // Pré-seleção por deep-link `?personId=` (cold com pessoa).
  useEffect(() => {
    if (queryPersonId !== null && selected === null) {
      setSelected({ id: queryPersonId, displayName: queryPersonId, subtitle: '' });
    }
  }, [queryPersonId, selected]);

  // Pré-seleção por contexto de origem (≠cold). `?personId=` tem precedência
  // determinística (pessoa explícita) caso ambos venham na URL.
  useEffect(() => {
    if (queryPersonId === null && context?.person != null && selected === null) {
      setSelected({
        id: context.person.id,
        displayName: context.person.displayName,
        subtitle: context.reasonHint,
      });
    }
  }, [context, selected, queryPersonId]);

  // Detalhe da pessoa selecionada (identidades + guardianship).
  const { data: person, isLoading: personLoading } = usePerson(selected?.id);

  // Nome real assim que o detalhe chega (deep-link só tinha o id).
  useEffect(() => {
    if (person !== undefined && selected !== null && selected.displayName === selected.id) {
      setSelected({
        id: person.id,
        displayName: person.displayName,
        subtitle:
          person.primaryIdentifierValue !== null && person.primaryIdentifierValue !== ''
            ? person.primaryIdentifierValue
            : selected.subtitle,
      });
    }
  }, [person, selected]);

  // Trocar de categoria invalida o template (e suas variáveis) escolhido na
  // categoria anterior — senão o Passo 4 mostraria um template de outra categoria.
  useEffect(() => {
    setTemplate(null);
    setVars({});
  }, [category]);

  const guardianship = useMemo(
    () => (person !== undefined ? deriveGuardianship(person.guardianshipsAsWard) : null),
    [person],
  );

  // R-G9 — o aviso ANTES do envio. Sem ele, o bloqueio seria um 422 no fim de 4 passos: hoje o
  // operador não tem NENHUMA superfície onde ver que a pessoa pediu para não ser procurada.
  //
  // ★ O botão NÃO é desabilitado por isto, e é deliberado: o FE INFORMA, o servidor DECIDE. Se o
  // FE negasse, um estado em cache envelhecido impediria o operador de procurar alguém que acabou
  // de revogar a restrição — negar trabalho legítimo por dado stale do cliente é a pior direção
  // possível, e não há override. A recusa vem da porta (422 `recipient_restricted`), que é
  // fail-closed e autoritativa; aqui só a razão fica à vista, duas vezes.
  const { data: restriction } = useRecipientStatus(selected?.id);
  const blockedByRestriction = restriction?.restricted === true;

  const initiate = useInitiateProactive();

  const goBack = (): void => {
    if (queryPersonId !== null) navigate(`/pessoas/${queryPersonId}`);
    else navigate('/inbox');
  };

  const canNext =
    (step === 1 && selected !== null && channel !== null) ||
    (step === 2 && category !== null) ||
    (step === 3 && template !== null) ||
    step === 4;

  const onSubmit = async (): Promise<void> => {
    if (selected === null || channel === null || category === null || template === null) return;
    setBlocked(null);
    try {
      const res = await initiate.mutateAsync({
        personId: selected.id,
        channel,
        originType: origin,
        ...(origin !== 'cold' && originId !== undefined && { originId }),
        metaCategory: category,
        templateId: template.name,
        ...(Object.keys(vars).length > 0 && { templateVariables: vars }),
      });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Conversa iniciada',
        description: `Mensagem proativa enviada para ${selected.displayName}.`,
      });
      navigate(`/atendimento/conversation/${res.conversationId}`);
    } catch (err) {
      if (err instanceof ProactiveBlockedError) {
        setBlocked(blockedMessage(err.code));
      } else {
        setBlocked('Não foi possível iniciar a conversa. Tente novamente.');
      }
    }
  };

  return (
    <CapabilityGate
      capability="initiate-proactive-conversation"
      fallback={
        <div className="proactive-page">
          <div className="proactive-overlay">
            <div className="modal" role="dialog">
              <div className="modal__body">
                <p>Você não tem permissão para iniciar conversas proativas neste tenant.</p>
                <button className="btn btn--secondary btn--md" type="button" onClick={goBack}>
                  Voltar
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    >
      <div className="proactive-page">
        <div className="inbox-bg" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="inbox-bg__row" />
          ))}
        </div>

        <div className="proactive-overlay">
          <div className="modal modal--xl" role="dialog" aria-label="Iniciar conversa proativa">
            <div className="modal__head">
              <Send size={14} style={{ color: 'var(--brand)' }} />
              <h2 className="modal__title">Iniciar conversa proativa</h2>
              <button
                className="btn btn--ghost btn--sm btn--icon"
                type="button"
                aria-label="Fechar"
                onClick={goBack}
              >
                <X size={14} />
              </button>
            </div>

            <div
              style={{
                padding: 'var(--space-1) var(--space-5)',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <StepRail step={step} />
            </div>

            <div className="modal__body">
              {step === 1 && (
                <Step1PessoaCanal
                  selected={selected}
                  onSelectPerson={(p) => {
                    setSelected(p);
                    setChannel(null);
                  }}
                  person={person}
                  personLoading={personLoading}
                  channel={channel}
                  onSelectChannel={setChannel}
                  restriction={restriction}
                />
              )}
              {step === 2 && <Step2Categoria category={category} onPick={setCategory} />}
              {step === 3 && category !== null && (
                <Step3Template
                  category={category}
                  templateId={template?.name ?? null}
                  onSelectTemplate={(t) => {
                    setTemplate(t);
                    setVars({});
                  }}
                  vars={vars}
                  onVar={(k, v) => setVars((prev) => ({ ...prev, [k]: v }))}
                />
              )}
              {step === 4 &&
                selected !== null &&
                channel !== null &&
                category !== null &&
                template !== null && (
                  <Step4Confirmar
                    person={selected}
                    channel={channel}
                    category={category}
                    templateName={template.name}
                    bodyPreview={template.bodyPreview}
                    vars={vars}
                    operatorName={user?.name ?? 'Operador'}
                    operatorRole={ROLE_LABELS[user?.primaryRole ?? ''] ?? user?.primaryRole ?? ''}
                    guardianship={guardianship}
                    blockedMessage={
                      blocked ??
                      (blockedByRestriction
                        ? `${recipientRestrictionMessage(restriction?.cause)} O envio está bloqueado.`
                        : null)
                    }
                  />
                )}
            </div>

            <div className="modal__foot">
              <button
                className="btn btn--ghost btn--md"
                type="button"
                onClick={() => {
                  setBlocked(null);
                  setStep((s) => Math.max(s - 1, 1));
                }}
                disabled={step === 1}
              >
                ← Anterior
              </button>
              <div style={{ flex: 1 }} />
              <span className="muted mono" style={{ fontSize: 10 }}>
                {step} / 4
              </span>
              {step < 4 ? (
                <button
                  className="btn btn--primary btn--md"
                  type="button"
                  onClick={() => setStep((s) => Math.min(s + 1, 4))}
                  disabled={!canNext}
                >
                  Próximo →
                </button>
              ) : (
                <button
                  className="btn btn--primary btn--md"
                  type="button"
                  onClick={() => void onSubmit()}
                  disabled={initiate.isPending}
                >
                  <Send size={12} />
                  {initiate.isPending ? 'Enviando…' : 'Enviar agora'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
